/**
 * YearOfTheThreadsSection
 *
 * 올해의 스레드 섹션 (Top 3)
 * - 조회수 60% + 댓글 20% + 리포스트 20% 종합 상위 3개 스레드
 * - MagazineSectionContainer + CoverStoryCard + LineGrid 패턴 사용
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import MagazineSectionContainer from '../magazine/MagazineSectionContainer';
import CoverStoryCard from '../magazine/CoverStoryCard';
import LineGrid from '../magazine/LineGrid';
import { useIsMobile } from '../../hooks/useIsMobile';
import YearEndSectionTitle from './YearEndSectionTitle';

// 올해의 스레드 영상 URL
const YEAR_OF_THREADS_VIDEO = 'https://firebasestorage.googleapis.com/v0/b/my-threads-insight.firebasestorage.app/o/yearEnd%2F2025%2Fyear_of_threads.mp4?alt=media';

export const YearOfTheThreadsSection = ({ yearOfTheThreads, ownerProfile }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useIsMobile('xl');

  // 데이터 구조 호환: 배열(신규) 또는 단일 객체(레거시) 처리
  const threads = (() => {
    // 신규 구조: { threads: [...], threadIds: [...] }
    if (Array.isArray(yearOfTheThreads?.threads)) {
      return yearOfTheThreads.threads;
    }
    // 레거시 구조: { thread: {...}, score: ..., viewsRank: ... }
    if (yearOfTheThreads?.thread) {
      return [{ thread: yearOfTheThreads.thread, score: yearOfTheThreads.score, viewsRank: yearOfTheThreads.viewsRank, engagementRate: yearOfTheThreads.engagementRate }];
    }
    return [];
  })();

  if (threads.length === 0) {
    return null;
  }

  return (
    <MagazineSectionContainer isMobile={isMobile}>
      <YearEndSectionTitle title={t('yearEnd.yearOfTheThreads.title')} />

      <LineGrid
        gap={0}
        borderColor="primary.main"
        gridColumns={isMobile ? 1 : 4}
        breakpoint="xl"
      >
        {/* 첫 번째 col: 타이틀 + 영상 (위아래 양쪽 정렬) */}
        <Grid size={{ xs: 12, xl: 3 }}>
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            {/* 타이틀 - CoverStoryCard 스타일 */}
            <Box sx={{ px: isMobile ? 2 : 3, py: isMobile ? 3 : 5 }}>
              <Typography
                variant={isMobile ? 'body1' : 'h5'}
                sx={{
                  fontFamily: '"RIDIBatang", "Noto Serif KR", serif',
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  lineHeight: isMobile ? 1.4 : 1.5,
                  wordBreak: 'keep-all',
                }}
              >
                {t('yearEnd.yearOfTheThreads.description', {
                  year: 2025,
                  username: ownerProfile?.username || '',
                  defaultValue: `2025년 ${ownerProfile?.username || ''}님의 스레드를 가장 빛낸 스레드 3개를 선정했습니다.`
                })}
              </Typography>
            </Box>
            {/* 영상 (1:1 비율) */}
            <Box
              sx={{
                width: '100%',
                aspectRatio: '1/1',
                overflow: 'hidden',
                bgcolor: 'grey.100',
              }}
            >
              <video
                src={YEAR_OF_THREADS_VIDEO}
                autoPlay
                loop
                muted
                playsInline
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Box>
          </Box>
        </Grid>

        {/* 나머지 col: 스레드 카드 */}
        {threads.map((threadData, index) => {
          const thread = threadData.thread || {};
          // CoverStoryCard용 user 객체 생성 (데이터 소유자 프로필 사용)
          const user = {
            id: ownerProfile?.id || '',
            profile: {
              username: ownerProfile?.username || '',
              name: ownerProfile?.name || ownerProfile?.username || '',
              threads_profile_picture_url: ownerProfile?.threads_profile_picture_url || null,
            },
          };

          return (
            <Grid key={thread.id || index} size={{ xs: 12, xl: 3 }}>
              <CoverStoryCard
                thread={thread}
                user={user}
                isMobile={isMobile}
              />
            </Grid>
          );
        })}
      </LineGrid>
    </MagazineSectionContainer>
  );
};

export default YearOfTheThreadsSection;
